import { convertDuration } from "@mountaya/elements/units";

/**
 * TIME_WINDOW_PAST_HOURS is how far before now the forecast window reaches, in hours.
 *
 * The time-aware overlays carry tiles from three days back. The window stops an hour short of
 * that on purpose: a request leaves at one instant and is checked against the server's clock at
 * a later one, so an instant a full 72 hours back would already be past the limit when it
 * arrives.
 */
export const TIME_WINDOW_PAST_HOURS = 71;

/** TIME_WINDOW_FUTURE_HOURS is how far after now the forecast window reaches, in hours: six days. */
export const TIME_WINDOW_FUTURE_HOURS = 144;

/**
 * TIME_OFFSET_MIN_MINUTES is the earliest overlay time offset the studio states, in minutes from
 * now (negative, the past): {@link TIME_WINDOW_PAST_HOURS} back. The URL's `time_offset` is
 * clamped to it on the way in and on the way out, so an offset never names an instant the
 * time-aware overlays carry no tiles for.
 */
export const TIME_OFFSET_MIN_MINUTES = -convertDuration(TIME_WINDOW_PAST_HOURS, "hour", "minute");

/**
 * TIME_OFFSET_MAX_MINUTES is the latest overlay time offset the studio states, in minutes from
 * now: {@link TIME_WINDOW_FUTURE_HOURS} ahead, clamped to as {@link TIME_OFFSET_MIN_MINUTES} is.
 */
export const TIME_OFFSET_MAX_MINUTES = convertDuration(TIME_WINDOW_FUTURE_HOURS, "hour", "minute");

/**
 * TimeSliderWindow is the forecast window laid out for a slider with one stop per hour, around
 * one instant taken as now.
 *
 * A slider value is a whole number of hours from the window's start, so value 0 is the earliest
 * instant the overlays hold and {@link TimeSliderWindow.maxValue} the latest.
 *
 * A window either has an instant at every stop of its track or has none at all, and
 * {@link TimeSliderWindow.startMs} says which: {@link timeSliderWindow} states when a window
 * has none.
 */
export interface TimeSliderWindow {
  /**
   * dateAt answers the instant a slider value stands for.
   *
   * It answers null when the window has no instants, and for a value that stands for none: one
   * that is not a number, or one so far off the track that a `Date` cannot hold its instant. So
   * what it answers is always safe to format and to serialize.
   */
  readonly dateAt: (value: number) => Date | null;
  /** The slider's last value: the window's whole length, in hours. */
  readonly maxValue: number;
  /** The slider value that stands for now. */
  readonly nowValue: number;
  /**
   * The instant slider value 0 stands for, in epoch milliseconds, or null when the window has
   * no instants.
   */
  readonly startMs: number | null;
  /**
   * valueAt answers the slider value of an overlay timestamp, an ISO 8601 instant, rounded to
   * the nearest hour. A null timestamp is the studio's spelling of "now" and answers
   * {@link TimeSliderWindow.nowValue}. An instant outside the window answers the nearest end, so
   * a stored instant the window has moved past still places the thumb on the track.
   *
   * A timestamp that cannot be placed answers {@link TimeSliderWindow.nowValue} too: one that
   * parses to no instant, and every timestamp when the window has no instants.
   */
  readonly valueAt: (iso: string | null) => number;
}

/**
 * instantAt answers the instant at `ms` epoch milliseconds, or null when a `Date` holds none
 * there.
 */
const instantAt = (ms: number): Date | null => {
  const date = new Date(ms);

  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * timeSliderWindow lays the forecast window out around `nowMs`, an instant in epoch
 * milliseconds, for a slider with one stop per hour.
 *
 * The window runs {@link TIME_WINDOW_PAST_HOURS} back and {@link TIME_WINDOW_FUTURE_HOURS}
 * ahead. It is the same window a shared link's `time_offset` is clamped to, so a slider and a
 * link can never name an instant the other refuses.
 *
 * `nowMs` is the caller's to supply: this reads no clock. A surface takes it from the studio's
 * `Clock` seam, so a host that pins or replays time moves the window with it.
 *
 * **A `nowMs` that is no instant.** A clock built on a start it could not parse reads `NaN`, and
 * a window cannot be laid out around that, around an infinite number, or around one so far out
 * that an end of the window is past what a `Date` holds. The track is laid out all the same,
 * since its stops are hours and its length is no clock's:
 * {@link TimeSliderWindow.maxValue} and {@link TimeSliderWindow.nowValue} are what they always
 * are. It carries no instants: {@link TimeSliderWindow.startMs} is null,
 * {@link TimeSliderWindow.dateAt} answers null for every value and
 * {@link TimeSliderWindow.valueAt} answers `nowValue` for every timestamp. A surface reads that
 * as a slider with nothing to place and nothing to commit, and the overlays stay on now.
 *
 * @example
 * ```ts
 * declare const overlayTimestamp: string | null;
 *
 * const window = timeSliderWindow(Date.now());
 * const value = window.valueAt(overlayTimestamp);
 * const timestamp = window.dateAt(value + 6)?.toISOString();
 * ```
 */
export const timeSliderWindow = (nowMs: number): TimeSliderWindow => {
  const maxValue = TIME_WINDOW_PAST_HOURS + TIME_WINDOW_FUTURE_HOURS;
  const firstMs = nowMs - convertDuration(TIME_WINDOW_PAST_HOURS, "hour", "millisecond");
  const lastMs = firstMs + convertDuration(maxValue, "hour", "millisecond");

  // Both ends are instants or the window has none: every stop lies between them, so a track
  // whose ends a Date holds has an instant at each of its stops.
  const startMs = instantAt(firstMs) !== null && instantAt(lastMs) !== null ? firstMs : null;

  return {
    dateAt: (value) =>
      startMs === null ? null : instantAt(startMs + convertDuration(value, "hour", "millisecond")),
    maxValue,
    nowValue: TIME_WINDOW_PAST_HOURS,
    startMs,
    valueAt: (iso) => {
      if (!iso || startMs === null) {
        return TIME_WINDOW_PAST_HOURS;
      }

      const hours = Math.round(
        convertDuration(new Date(iso).getTime() - startMs, "millisecond", "hour"),
      );

      // A timestamp that parses to no instant has no hour, and no end of the track is nearer.
      return Number.isNaN(hours) ? TIME_WINDOW_PAST_HOURS : Math.min(maxValue, Math.max(0, hours));
    },
  };
};
